import React, { useState } from 'react';
import { Service } from '../types/booking.types';
import { apiCall } from '../../../../utils/apiClient';

interface CreateJobState {
    jobTitle: string;
    scheduledDate: string;
    notes: string;
}

interface CreateJobModalProps {
    isOpen: boolean;
    onClose: () => void;
    onCreate: (createdJob?: any) => void;
    availableServices: Service[];
    selectedServices: number[];
    toggleServiceSelection: (serviceId: number) => void;
    newJob: CreateJobState;
    setNewJob: React.Dispatch<React.SetStateAction<CreateJobState>>;
    bookingId?: number;
}

const toNumber = (value: unknown, fallback = 0): number => {
    if (value === null || value === undefined || value === '') return fallback;
    const parsed = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const toBool = (value: unknown): boolean => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') return value === '1' || value.toLowerCase() === 'true';
    return false;
};

const calculateServiceTotal = (service: Service): number => {
    const serviceCost = toNumber(service.partner_price) * toNumber(service.quantity);
    const addonsCost = (service.addons || []).reduce((total, addon) => {
        return total + toNumber(addon.partner_price) * toNumber(addon.quantity, 1);
    }, 0);
    return serviceCost + addonsCost;
};

const calculateSelectedTotal = (services: Service[], selectedIds: number[]): number => {
    return services
        .filter((service) => selectedIds.includes(service.id))
        .reduce((total, service) => total + calculateServiceTotal(service), 0);
};

const CreateJobModal: React.FC<CreateJobModalProps> = ({
    isOpen,
    onClose,
    onCreate,
    availableServices,
    selectedServices,
    toggleServiceSelection,
    newJob,
    setNewJob,
    bookingId,
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    if (!isOpen) return null;

    const selectedTotal = calculateSelectedTotal(availableServices, selectedServices);

    const handleCreateJob = async () => {
        if (!newJob.jobTitle || !newJob.scheduledDate || selectedServices.length === 0) {
            setError('Please fill required fields and select at least one service');
            return;
        }

        if (!bookingId) {
            setError('Booking ID is required');
            return;
        }

        const adminIdRaw = localStorage.getItem('id');
        const adminId = adminIdRaw ? Number(adminIdRaw) : null;
        if (!adminId || Number.isNaN(adminId)) {
            setError('Admin ID is required');
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            // Prepare the job data for API (partner-job/create payload)
            const jobData = {
                booking_id: bookingId,
                assigned_by_admin_id: adminId,
                title: newJob.jobTitle,
                notes: newJob.notes,
                estimated_start_date: newJob.scheduledDate,
                items: selectedServices
                    .map((serviceId) => {
                        const service = availableServices.find((s) => s.id === serviceId);
                        if (!service) return null;

                        const serviceRate = toNumber(service.partner_price);
                        const serviceQty = toNumber(service.quantity);
                        const serviceAmount = serviceRate * serviceQty;

                        return {
                            service_source: toBool(service.is_additional) ? 'additional_service' : 'booking_service',
                            source_id: service.id,
                            room_id: service.room_id,
                            title: service.service_name,
                            quantity: service.quantity,
                            unit: service.unit,
                            rate: service.partner_price,
                            amount: serviceAmount,
                            with_material: toBool(service.with_material),
                            addons: (service.addons || []).map((addon) => {
                                const addonRate = toNumber(addon.partner_price);
                                const addonQty = toNumber(addon.quantity, 1);
                                return {
                                    source_id: addon.id,
                                    name: addon.addon_name,
                                    quantity: addon.quantity,
                                    unit: addon.unit,
                                    rate: addon.partner_price,
                                    amount: addonRate * addonQty,
                                    with_material: toBool(addon.with_material),
                                };
                            }),
                        };
                    })
                    .filter(Boolean),
            };

            const response = await apiCall({
                endpoint: 'partner-jobs/create',
                method: 'POST',
                data: jobData,
            });

            if (response.status === 200 || response.status === 201) {
                onCreate(response.data);
            } else {
                setError(response.message || 'Failed to create job');
            }
        } catch (err: any) {
            setError(err.message || 'An error occurred while creating the job');
            console.error('Create job error:', err);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
            onClick={onClose}
        >
            <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="px-6 py-4 border-b flex justify-between items-center">
                    <h3 className="text-lg font-bold text-gray-800">Create New Job</h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-xl">
                        ✕
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    <div>
                        <label className="block mb-2 text-sm font-semibold text-gray-700">
                            Job Title <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={newJob.jobTitle}
                            onChange={(e) => setNewJob({ ...newJob, jobTitle: e.target.value })}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:outline-none"
                            placeholder="e.g., False Ceiling Work, Painting Work"
                        />
                    </div>

                    <div>
                        <label className="block mb-2 text-sm font-semibold text-gray-700">
                            Select Services <span className="text-red-500">*</span>
                        </label>
                        <p className="text-xs text-gray-500 mb-3">Select one or more services to include in this job</p>
                        <div className="space-y-2 max-h-72 overflow-y-auto border border-gray-200 rounded p-3">
                            {availableServices.length === 0 ? (
                                <p className="text-sm text-gray-500 text-center py-4">No services available to add</p>
                            ) : (
                                availableServices.map((service) => {
                                    const isChecked = selectedServices.includes(service.id);
                                    const serviceCost = toNumber(service.partner_price) * toNumber(service.quantity);
                                    const addonsCost = (service.addons || []).reduce((total, addon) => {
                                        return total + toNumber(addon.partner_price) * toNumber(addon.quantity, 1);
                                    }, 0);
                                    const serviceTotal = serviceCost + addonsCost;

                                    return (
                                        <label
                                            key={service.id}
                                            className={`flex items-start p-3 border rounded cursor-pointer hover:bg-gray-50 transition-colors ${isChecked ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                                                }`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isChecked}
                                                onChange={() => toggleServiceSelection(service.id)}
                                                className="mt-1 mr-3"
                                            />
                                            <div className="flex-1">
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <p className="font-medium text-gray-800 text-sm">{service.service_name}</p>
                                                        <p className="text-xs text-gray-500">
                                                            {service.quantity} {service.unit}
                                                        </p>
                                                        {toNumber(service.partner_price) > 0 && (
                                                            <p className="text-xs text-blue-600 font-semibold mt-1">
                                                                Partner Rate ₹{toNumber(service.partner_price).toLocaleString('en-IN')}{' '}/{service.unit}
                                                                <span className="text-gray-500 font-normal">
                                                                    {toBool(service.with_material) ? ' (with material)' : ' (without material)'}
                                                                </span>
                                                            </p>
                                                        )}
                                                        {toNumber(service.partner_price) > 0 && (
                                                            <p className="text-xs text-gray-600 mt-1">
                                                                Amount: ₹{serviceCost.toLocaleString('en-IN')} ({toNumber(service.quantity)} × {toNumber(service.partner_price).toLocaleString('en-IN')})
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="text-xs text-gray-500">Service Total</p>
                                                        <p className="text-sm font-semibold text-gray-900">
                                                            ₹{serviceTotal.toLocaleString('en-IN')}
                                                        </p>
                                                    </div>
                                                </div>

                                                {service.addons && service.addons.length > 0 && (
                                                    <div className="mt-2 border-t border-gray-200 pt-2">
                                                        <div className="space-y-2">
                                                            {service.addons.map((addon) => {
                                                                const addonTotal = toNumber(addon.partner_price) * toNumber(addon.quantity, 1);
                                                                return (
                                                                    <div key={addon.id} className="bg-blue-50 border border-blue-100 rounded p-2">
                                                                        <div className="flex justify-between items-start">
                                                                            <div className="flex-1">
                                                                                <p className="text-xs font-semibold text-gray-800">{addon.addon_name}</p>
                                                                                <p className="text-xs text-gray-600 mt-0.5">Qty: {addon.quantity} {addon.unit}</p>
                                                                                {toNumber(addon.partner_price) > 0 && (
                                                                                    <p className="text-xs text-blue-700 font-semibold mt-1">
                                                                                        ₹{toNumber(addon.partner_price).toLocaleString('en-IN')}/{addon.unit}
                                                                                        <span className="text-gray-600 font-normal ml-1">
                                                                                            {toBool(addon.with_material) ? '(with mat.)' : '(w/o mat.)'}
                                                                                        </span>
                                                                                    </p>
                                                                                )}
                                                                                {toNumber(addon.partner_price) > 0 && (
                                                                                    <p className="text-xs text-gray-600 mt-1">
                                                                                        Amount: ₹{addonTotal.toLocaleString('en-IN')} ({toNumber(addon.quantity, 1)} × {toNumber(addon.partner_price).toLocaleString('en-IN')})
                                                                                    </p>
                                                                                )}
                                                                            </div>
                                                                            <div className="text-right text-xs">
                                                                                <p className="font-semibold text-blue-700">₹{addonTotal.toLocaleString('en-IN')}</p>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </label>
                                    );
                                })
                            )}
                        </div>
                        {selectedServices.length > 0 && (
                            <div className="mt-2 flex justify-between items-center text-xs">
                                <span className="text-green-600">✓ {selectedServices.length} service(s) selected</span>
                                <span className="text-gray-700 font-semibold">
                                    Selected Total: ₹{selectedTotal.toLocaleString('en-IN')}
                                </span>
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="block mb-2 text-sm font-semibold text-gray-700">
                            Scheduled Date <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="date"
                            value={newJob.scheduledDate}
                            onChange={(e) => setNewJob({ ...newJob, scheduledDate: e.target.value })}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:outline-none"
                        />
                    </div>

                    <div>
                        <label className="block mb-2 text-sm font-semibold text-gray-700">
                            Notes <span className="text-gray-500">(optional)</span>
                        </label>
                        <textarea
                            value={newJob.notes}
                            onChange={(e) => setNewJob({ ...newJob, notes: e.target.value })}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:outline-none resize-none"
                            rows={3}
                            placeholder="Add any additional notes..."
                        />
                    </div>

                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
                            {error}
                        </div>
                    )}
                </div>

                <div className="px-6 py-4 border-t flex justify-between items-center">
                    <div className="text-sm text-gray-700">
                        <span className="font-semibold">Total:</span> ₹{selectedTotal.toLocaleString('en-IN')}
                    </div>
                    <div className="flex gap-3">
                        <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50">
                            Cancel
                        </button>
                        <button
                            onClick={handleCreateJob}
                            disabled={!newJob.jobTitle || !newJob.scheduledDate || selectedServices.length === 0 || isSubmitting}
                            className="px-6 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSubmitting ? 'Creating...' : 'Create Job'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateJobModal;
